import { makeObservable, observable, runInAction, computed, action } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';

/**
 * The details of a current order
 */
class FolderEditDialogStore {
    private sdk: ApplicationSdk;
    public folder_id: number | null = null;
    public parent_id: number | null = null;
    public folder_loading: boolean = false;

    public folder_image: any = null;
    public folder_image_uuid: string | null = null;

    public folder_name: string = '';
    public folder_description: string = '';
    public folder_image_url: string | null = null;
    public folder_visible: boolean = true;

    public parent_folders: any[] = [];

    public s3url: string = '';

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            folder_loading: observable,
            folder_id: observable,
            parent_id: observable,
            folder_name: observable,
            folder_description: observable,
            folder_image: observable,
            folder_image_uuid: observable,
            folder_image_url: observable,
            folder_visible: observable,
            parent_folders: observable,
            toggleVisible: action,
            setImage: action,
            setParentId: action,
            isFolderNameValid: computed,
            isValid: computed,
            folderName: computed,
            folderDescription: computed,
            image: computed,
            visibleForAll: computed,
            hasParent: computed,
            currentParentFolder: computed,
            allParentFolders: computed,
            isLoading: computed,
        });

        this.sdk = sdk;
    }

    get isLoading(): boolean {
        return this.folder_loading;
    }

    public async init(config: any): Promise<void> {
        this.parent_id = config?.parent_id || null;
        this.folder_id = config?.folder_id || null;

        if (!this.isNewFolder) {
            this.fetchFolder();
        }
    }

    public async fetchFolder(): Promise<void> {
        runInAction(() => {
            this.folder_loading = true;
        });

        const folderListResult: any = await this.sdk.fetchApi('training', 'folder/get', {
            folder_id: this.folder_id,
        });

        runInAction(() => {
            this.folder_loading = false;
        });

        if (folderListResult?.status === 200) {
            runInAction(() => {
                const { folder, s3url, folderList } = folderListResult?.json || {};

                if (folder) {
                    this.folder_name = folder.name || '';
                    this.folder_description = folder.description || '';
                    this.folder_image_url = folder?.image || null;
                    this.parent_id = folder.parent_id || null;
                    this.folder_visible = folder.visible || false;
                    this.parent_folders = folderList || [];

                    if (folder.image) {
                        this.folder_image_uuid = folder.image;
                    }

                    if (this.folder_image_url && this.folder_image_url.includes('icon:')) {
                        this.folder_image_url = null;
                    }
                }

                this.s3url = s3url || '';
            });
        }
    }

    public async createOrUpdateFolder(): Promise<any> {
        if (this.isValid) {
            runInAction(() => {
                this.folder_loading = true;
            });
            // 1. - Upload image if it is a new one
            if (this.folder_image) {
                const uploadResult = await this.sdk.uploadImage(
                    'folder',
                    'upload',
                    this.folder_image
                );
                if (uploadResult?.json?.fileId) {
                    this.folder_image_uuid = uploadResult.json.fileId;
                }
            }

            // 2. - Upload image to S3
            const folderData = {
                parent_id: this.parent_id || null,
                name: this.folderName,
                description: this.folderDescription,
                visible: this.visibleForAll,
                folder_id: this.isNewFolder ? undefined : this.folder_id,
                image: this.folder_image_uuid,
            };

            const response = await this.sdk.fetchApi(
                'training',
                this.isNewFolder ? 'folder/add' : 'folder/update',
                folderData
            );

            runInAction(() => {
                this.folder_loading = false;
            });

            if (response.status === 201 || response.status === 200) {
                return {
                    id: response.json.id,
                    event: {
                        event: this.isNewFolder ? 'NEW' : 'UPDATE',
                    },
                };
            }
        }
        return {
            id: null,
            event: null,
        };
    }

    public setParentId(id: number): void {
        runInAction(() => {
            if (this.parent_folders.find(folder => folder.id === id)) {
                this.parent_id = id;
            }
        });
    }

    get currentParentFolder(): any {
        return this.parent_folders.find(folder => folder.id === this.parent_id) || null;
    }

    get allParentFolders(): any[] {
        return this.parent_folders.map(folder => {
            return {
                id: folder.id,
                name: folder.name,
            };
        });
    }

    public toggleVisible(): void {
        this.folder_visible = !this.folder_visible;
    }

    get image(): any {
        return this.folder_image;
    }

    get imageBlob(): string {
        if (!this.folder_image_url) {
            return '';
        }
        return `${this.s3url}${this.folder_image_url}.jpeg`;
    }

    public setImage(img: any): void {
        this.folder_image = img;
    }

    set folderName(name: string) {
        // limit to 80 characters
        if (name.length <= 80) {
            runInAction(() => {
                this.folder_name = name;
            });
        }
    }

    get folderName(): string {
        return this.folder_name || '';
    }

    get isFolderNameValid(): boolean {
        return this.folderName.length >= 3 && this.folderName.length <= 80;
    }

    set folderDescription(txt: string) {
        // limit to 80 characters
        if (txt.length <= 250) {
            runInAction(() => {
                this.folder_description = txt;
            });
        }
    }

    get folderDescription(): string {
        return this.folder_description || '';
    }

    get id(): string {
        return 'editfolder';
    }

    get isNewFolder(): boolean {
        return this.folder_id === null;
    }

    get buttonLabel(): string {
        return this.isNewFolder ? 'Ordner erstellen' : 'Ordner speichern';
    }

    get header(): string {
        return this.isNewFolder ? 'Neuer Ordner' : 'Ordner bearbeiten';
    }

    get isValid(): boolean {
        return this.isFolderNameValid;
    }

    get visibleForAll(): boolean {
        return this.folder_visible;
    }

    get hasParent(): boolean {
        return !!this.parent_id;
    }
}

export default FolderEditDialogStore;
